/**
 * useClickOutside Hook
 *
 * Detects clicks outside a referenced element and triggers a callback.
 * Useful for closing dropdowns, modals, and other dismissible UI elements.
 */

import { useEffect, type RefObject } from 'react';

/**
 * Hook that triggers a callback when clicking outside the referenced element
 *
 * @param ref - Reference to the element to detect clicks outside of
 * @param handler - Callback function to run when outside click is detected
 * @param enabled - Whether the hook is active (default: true)
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T>,
  handler: () => void,
  enabled: boolean = true
): void {
  useEffect(() => {
    if (!enabled) return;

    function handleClickOutside(event: MouseEvent | TouchEvent) {
      // Check if click was outside the referenced element
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    }

    // Use mousedown and touchstart for immediate response
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, handler, enabled]);
}
